/**
 * ExportReportModal
 *
 * Two-step modal for exporting an EQuIP performance report:
 *   Step 1: Choose delivery method (Download or Email)
 *   Step 2: Processing spinner with cancel support
 *
 * Non-dismissible during processing (no backdrop click, Escape, or X).
 * Download triggers a browser file save; Email shows a brief success state.
 */

import { useState, useRef, useEffect } from 'react'
import { Download, Mail, Loader2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { equipApi } from '@/services/equipApi'

type DeliveryMethod = 'download' | 'email'
type ModalStep = 'choose' | 'email-form' | 'processing' | 'success' | 'error'

interface ExportReportModalProps {
  open: boolean
  onClose: () => void
  providerEmail: string
  providerName: string
  year: number
  quarter: number
}

export function ExportReportModal({
  open,
  onClose,
  providerEmail,
  providerName,
  year,
  quarter,
}: ExportReportModalProps) {
  const [step, setStep] = useState<ModalStep>('choose')
  const [method, setMethod] = useState<DeliveryMethod | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [toEmail, setToEmail] = useState(providerEmail)
  const [ccEmail, setCcEmail] = useState('')
  const [sentTo, setSentTo] = useState('')
  const abortRef = useRef<AbortController | null>(null)

  // Keep toEmail in sync when providerEmail prop changes
  useEffect(() => {
    setToEmail(providerEmail)
  }, [providerEmail])

  const resetAndClose = () => {
    setStep('choose')
    setMethod(null)
    setErrorMessage('')
    setToEmail(providerEmail)
    setCcEmail('')
    setSentTo('')
    abortRef.current = null
    onClose()
  }

  const handleCancel = () => {
    abortRef.current?.abort()
    resetAndClose()
  }

  const handleSelect = (selected: DeliveryMethod) => {
    setMethod(selected)
    if (selected === 'email') {
      setStep('email-form')
    } else {
      executeExport(selected)
    }
  }

  const handleSendEmail = () => {
    executeExport('email')
  }

  const executeExport = async (selected: DeliveryMethod) => {
    setStep('processing')
    const controller = new AbortController()
    abortRef.current = controller

    try {
      if (selected === 'download') {
        const blob = await equipApi.export.download(
          providerEmail, year, quarter, controller.signal
        )
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const safeName = providerName.replace(/[^a-zA-Z0-9 ]/g, '_').replace(/\s+/g, '_')
        a.download = `EQuIP_Report_Q${quarter}_${year}_${safeName}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        resetAndClose()
      } else {
        const trimmedTo = toEmail.trim() || providerEmail
        setSentTo(trimmedTo)
        await equipApi.export.email(
          providerEmail, year, quarter, controller.signal,
          trimmedTo, ccEmail.trim() || undefined,
        )
        setStep('success')
        setTimeout(resetAndClose, 2000)
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred')
      setStep('error')
    }
  }

  // Prevent closing during processing
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && step === 'processing') return
    if (!nextOpen) resetAndClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <AnimatePresence mode="wait">
          {/* ── Step 1: Choose delivery method ── */}
          {step === 'choose' && (
            <motion.div
              key="choose"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <DialogHeader>
                <DialogTitle>Download Report</DialogTitle>
                <DialogDescription>
                  Export your Q{quarter} {year} performance data as an Excel workbook.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-3 py-4">
                <button
                  onClick={() => handleSelect('download')}
                  className="flex items-center gap-3 rounded-lg border border-border p-4 text-left transition-colors hover:bg-accent/50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Download className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Download</div>
                    <div className="text-xs text-muted-foreground">
                      Save the Excel file to your device
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleSelect('email')}
                  className="flex items-center gap-3 rounded-lg border border-border p-4 text-left transition-colors hover:bg-accent/50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Email</div>
                    <div className="text-xs text-muted-foreground">
                      Send the report via email
                    </div>
                  </div>
                </button>
              </div>
            </motion.div>
          )}

          {/* ── Step 1b: Email form ── */}
          {step === 'email-form' && (
            <motion.div
              key="email-form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <DialogHeader>
                <DialogTitle>Email Report</DialogTitle>
                <DialogDescription>
                  Send {providerName}'s Q{quarter} {year} report via email.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="to-email">To</Label>
                  <Input
                    id="to-email"
                    type="email"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    placeholder="recipient@eventuswh.com"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="cc-email">CC <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input
                    id="cc-email"
                    type="email"
                    value={ccEmail}
                    onChange={(e) => setCcEmail(e.target.value)}
                    placeholder="manager@eventuswh.com"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="ghost" size="sm" onClick={() => setStep('choose')}>
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Back
                </Button>
                <Button size="sm" onClick={handleSendEmail} disabled={!toEmail.trim()}>
                  <Mail className="mr-1 h-4 w-4" />
                  Send
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── Step 2: Processing ── */}
          {step === 'processing' && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col items-center gap-4 py-8"
            >
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-center">
                <p className="text-sm font-medium">Generating your report...</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  This may take a moment
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            </motion.div>
          )}

          {/* ── Success (email only) ── */}
          {step === 'success' && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center gap-3 py-8"
            >
              <CheckCircle2 className="h-10 w-10 text-success" />
              <p className="text-sm font-medium">Report sent!</p>
              <p className="text-xs text-muted-foreground">{sentTo}</p>
            </motion.div>
          )}

          {/* ── Error ── */}
          {step === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col items-center gap-4 py-8"
            >
              <AlertCircle className="h-10 w-10 text-destructive" />
              <div className="text-center">
                <p className="text-sm font-medium">Something went wrong</p>
                <p className="mt-1 text-xs text-muted-foreground">{errorMessage}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={resetAndClose}>
                  Close
                </Button>
                <Button size="sm" onClick={() => method && handleSelect(method)}>
                  Try Again
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  )
}
